import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';

import CircularProgress from '@mui/material/CircularProgress'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IconButton from '@mui/material/IconButton'
import Paginator from 'cityads-v3/components/Paginator/Paginator'
import RegionsCollapser from 'cityads-v3/components/Collapser/RegionsCollapser';
import Table from 'cityads-v3/components/Table/Table';
import TargetValueFormat, { getTargetValue } from 'cityads-v3/components/Format/TargetValueFormat';
import TextCollapser from 'cityads-v3/components/Collapser/TextCollapser';

import classes from './Terms.scss';

@withTranslation(['Offers'], { i18n, wait: false })
export default class Payments extends Component {

  static propTypes = {
    t: PropTypes.func.isRequired,
    targets: PropTypes.oneOfType([PropTypes.object, PropTypes.arrayOf(PropTypes.object)]),
  }

  state = {
    tablePage: 1,
    tablePerPage: 10,
    rowsExpandedByTargetId: {},
    namesTruncatedByTargetId: {},
    regionsTruncatedByTargetId: {},
  }

  setTablePage(tablePage) {
    this.setState({ tablePage });
  }

  setTablePerPage(tablePerPage) {
    this.setState({ tablePerPage });
  }

  toggleRowExpanded = (targetId) => {
    this.setState(({ rowsExpandedByTargetId, namesTruncatedByTargetId, regionsTruncatedByTargetId }) => ({
      rowsExpandedByTargetId: { ...rowsExpandedByTargetId, [targetId]: !rowsExpandedByTargetId[targetId]},
      // if collapser was expanded, set that it is now truncated right away to avoid glitching of collapser button
      namesTruncatedByTargetId: {
        ...namesTruncatedByTargetId,
        [targetId]: rowsExpandedByTargetId[targetId] ? true : rowsExpandedByTargetId[targetId],
      },
      regionsTruncatedByTargetId: {
        ...regionsTruncatedByTargetId,
        [targetId]: rowsExpandedByTargetId[targetId] ? true : rowsExpandedByTargetId[targetId],
      },
    }));
  }

  setNamesTruncated = (targetId, truncated) => {
    if (this.state.namesTruncatedByTargetId[targetId] !== truncated) {
      this.setState(({ namesTruncatedByTargetId }) => ({
        namesTruncatedByTargetId: { ...namesTruncatedByTargetId, [targetId]: truncated}
      }));
    }
  }

  setRegionsTruncated = (targetId, truncated) => {
    if (this.state.regionsTruncatedByTargetId[targetId] !== truncated) {
      this.setState(({ regionsTruncatedByTargetId }) => ({
        regionsTruncatedByTargetId: { ...regionsTruncatedByTargetId, [targetId]: truncated}
      }));
    }
  }

  getTableCols() {
    const { t } = this.props;

    return [{
      name: 'id',
      title: '#',
      classes: { root: classes.paymentTableNumber }
    }, {
      name: 'name',
      title: t('target'),
      classes: { root: classes.paymentTableTarget }
    }, {
      name: 'geo',
      title: t('region'),
      classes: { root: classes.paymentTableGeo }
    }, {
      name: 'tariff',
      title: t('targetTariff'),
      classes: { root: classes.paymentTableTariff }
    }, {
      name: 'collapse',
      title: null,
      classes: { root: classes.paymentTableCollapse }
    }];
  }

  render() {
    const { t, targets } = this.props;
    const isLoading = !targets || targets.isLoading;

    if (isLoading) {
      return (
        <div className={classNames(classes.paymentTable, classes.paymentTableLoading)}>
          <CircularProgress />
        </div>
      );
    }

    const { tablePage, tablePerPage, rowsExpandedByTargetId, namesTruncatedByTargetId, regionsTruncatedByTargetId } = this.state;

    const start = (tablePage - 1) * tablePerPage;
    const end = start + tablePerPage;

    const data = targets.slice(start, end);

    return (
      <div className={classes.paymentTable} data-term-value="payments">
        <Table
          cols={this.getTableCols()}
          rows={data.map((target, num) => ({
            id  : (tablePage - 1) * tablePerPage + num + 1,
            name: (
              <TextCollapser
                lines = {2}
                expanded   = {rowsExpandedByTargetId[target.id]}
                onTruncate = {truncated => this.setNamesTruncated(target.id, truncated)}
                onClick    = {() => (namesTruncatedByTargetId[target.id] || regionsTruncatedByTargetId[target.id] || rowsExpandedByTargetId[target.id]) && this.toggleRowExpanded(target.id)}
              >
                {target.webmasters.length ? (
                  <>
                    <strong>{t('targetPersonal')}</strong>{' '}
                  </>
                ) : null}
                <span title={target.text}>{target.text}</span>
              </TextCollapser>
            ),
            geo : ((target.service_regions && target.service_regions.length) ?
              (
                <RegionsCollapser
                  ids        = {target.service_regions}
                  lines      = {2}
                  expanded   = {rowsExpandedByTargetId[target.id]}
                  onTruncate = {truncated => this.setRegionsTruncated(target.id, truncated)}
                  onClick    = {() => (namesTruncatedByTargetId[target.id] || regionsTruncatedByTargetId[target.id] || rowsExpandedByTargetId[target.id]) && this.toggleRowExpanded(target.id)}
                />
              ) :
              t('worldwide')
            ),
            tariff  : (<TargetValueFormat {...getTargetValue(target)} />),
            collapse: (
              (namesTruncatedByTargetId[target.id] || regionsTruncatedByTargetId[target.id] || rowsExpandedByTargetId[target.id]) ?
                (
                  <IconButton
                    className = {classNames(classes.expandButton, { [classes.expandButtonExpanded]: rowsExpandedByTargetId[target.id] })}
                    onClick={() => this.toggleRowExpanded(target.id)}
                    size="large"
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                ) :
                null
            )
          }))}
          showRowHover={true}
          blueTheme={true}
          scrollX={true}
          paginatorComponent={
            <Paginator
              count               = { targets.length }
              page                = { tablePage }
              rowsPerPage         = { tablePerPage }
              onPageChange        = { (evt, number) => this.setTablePage(number) }
              onRowsPerPageChange = { perpage => this.setTablePerPage(perpage) }
            />
          }
        />
      </div>
    );
  }

}
